// Маршруты приложения (PLAN.md, B2a). React Router в режиме данных, только
// в браузере: ни SSR, ни серверных loaders — статика за Caddy, данные идут
// через data/source.ts, как и раньше.
//
// Id страниц те же, что были в хэше (`#dist`, `#rooms`…): путь — это `/<id>`,
// Редактор — корень. Старые ссылки с хэшем переводятся один раз при загрузке
// (legacyHashPath), без записи в историю.

import type { ComponentType } from 'react';
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router';
import { Overview } from './pages/Overview';
import { Distributions } from './pages/Distributions';
import { Compare } from './pages/Compare';
import { Anomalies } from './pages/Anomalies';
import { Telemetry } from './pages/Telemetry';
import { Bestiary } from './pages/Bestiary';
import { Rooms } from './pages/Rooms';
import { Items } from './pages/Items';
import { Tuning } from './pages/Tuning';
import { Workspace } from './pages/Workspace';
import { Settings } from './pages/Settings';

export interface PageEntry {
  id: string;
  title: string;
  Component: ComponentType;
}

export const PAGES: readonly PageEntry[] = [
  { id: 'workspace', title: 'Редактор', Component: Workspace },
  { id: 'overview', title: 'Обзор', Component: Overview },
  { id: 'items', title: 'Предметы', Component: Items },
  { id: 'rooms', title: 'Комнаты', Component: Rooms },
  { id: 'bestiary', title: 'Враги и лут', Component: Bestiary },
  { id: 'tuning', title: 'Тюнинг', Component: Tuning },
  { id: 'dist', title: 'Распределения', Component: Distributions },
  { id: 'compare', title: 'Сравнение конфигов', Component: Compare },
  { id: 'anomalies', title: 'Аномалии', Component: Anomalies },
  { id: 'telemetry', title: 'Телеметрия', Component: Telemetry },
  { id: 'settings', title: 'Настройки', Component: Settings },
];

const HOME = 'workspace';

/** Путь страницы: Редактор — корень, остальные — `/<id>`. */
export function pagePath(id: string): string {
  return id === HOME ? '/' : `/${id}`;
}

/** Страница по пути; неизвестный путь — null (маршрут `*` уводит на Обзор). */
export function pageIdForPath(pathname: string): string | null {
  const id = pathname.replace(/^\/+|\/+$/g, '') || HOME;
  return PAGES.some((page) => page.id === id) ? id : null;
}

/**
 * Куда перевести старую ссылку `/#rooms`. null — переводить нечего:
 * хэша нет, страница уже задана путём или такого id никогда не было.
 */
export function legacyHashPath(pathname: string, hash: string): string | null {
  if (pathname !== '/' || hash.length < 2) return null;
  const id = hash.slice(1);
  return PAGES.some((page) => page.id === id) ? pagePath(id) : null;
}

/** Дерево маршрутов: Layout рисует шапку и <Outlet />, страницы — дети. */
export function buildRoutes(Layout: ComponentType, pages: readonly PageEntry[] = PAGES): RouteObject[] {
  return [
    {
      path: '/',
      Component: Layout,
      children: [
        ...pages.map((page): RouteObject =>
          page.id === HOME ? { index: true, Component: page.Component } : { path: page.id, Component: page.Component },
        ),
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
  ];
}

/** Роутер создаётся один раз вне дерева React — так требует RouterProvider. */
export function createAppRouter(Layout: ComponentType) {
  if (typeof window !== 'undefined') {
    const target = legacyHashPath(window.location.pathname, window.location.hash);
    if (target !== null) window.history.replaceState(window.history.state, '', target + window.location.search);
  }
  return createBrowserRouter(buildRoutes(Layout));
}
